import { useState, useEffect } from 'react';
import { X, Check, Loader2 } from 'lucide-react';
import { Portal } from './Portal';
import { useLanguage } from '../contexts/LanguageContext';
import { log } from '../lib/logger';

interface LanguageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export function LanguageDialog({ open, onOpenChange, onSaved }: LanguageDialogProps) {
  const { language: currentLanguage, setLanguage, t, translateError } = useLanguage();
  const [selectedLanguage, setSelectedLanguage] = useState<'en' | 'pt'>(currentLanguage);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (open) {
      setSelectedLanguage(currentLanguage);
      setError('');
    }
  }, [open, currentLanguage]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onOpenChange]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onOpenChange(false);
    }
  };

  const handleSave = async () => {
    if (selectedLanguage === currentLanguage) {
      onOpenChange(false);
      return;
    }

    try {
      setSaving(true);
      setError('');
      await setLanguage(selectedLanguage);
      onSaved();
      onOpenChange(false);
    } catch (err) {
      log.error('Error saving language:', err);
      setError(translateError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Portal>
      <div
        data-testid="language-dialog-backdrop"
        className="fixed inset-0 z-50 bg-gray-900/40 backdrop-blur-sm backdrop-blur-sm"
        onClick={handleBackdropClick}
      />
      <div
        data-testid="language-dialog-content"
        className="fixed z-50 inset-0 grid place-items-center p-4"
        onClick={handleBackdropClick}
      >
        <div className="bg-white rounded-3xl shadow-2xl ring-1 ring-gray-100 w-full max-w-md">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 id="language-dialog-title" className="text-xl font-semibold text-gray-900">
              {t('language.modal.title')}
            </h2>
            <button
              onClick={() => onOpenChange(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close dialog"
              type="button"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-3">
            <button
              type="button"
              onClick={() => setSelectedLanguage('en')}
              className={`w-full px-4 py-3 rounded-lg border-2 transition-all flex items-center justify-between ${
                selectedLanguage === 'en'
                  ? 'border-green-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className="text-sm font-medium text-gray-900">
                {t('language.english')}
              </span>
              {selectedLanguage === 'en' && (
                <Check className="w-5 h-5 text-blue-600" />
              )}
            </button>

            <button
              type="button"
              onClick={() => setSelectedLanguage('pt')}
              className={`w-full px-4 py-3 rounded-lg border-2 transition-all flex items-center justify-between ${
                selectedLanguage === 'pt'
                  ? 'border-green-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className="text-sm font-medium text-gray-900">
                {t('language.portuguese')}
              </span>
              {selectedLanguage === 'pt' && (
                <Check className="w-5 h-5 text-blue-600" />
              )}
            </button>

            {error && (
              <div
                className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
                role="alert"
                aria-live="polite"
              >
                {error}
              </div>
            )}
          </div>

          <div className="flex gap-3 p-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={saving}
            >
              {t('language.modal.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 hover:scale-[1.02]"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              <span>{saving ? t('language.modal.saving') : t('language.modal.save')}</span>
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}
